<template>
  <section class="legal-page page-shell">
    <button class="legal-back" @click="navigate('home')">← Back to CalmDecide</button>

    <article v-if="page === 'privacy'">
      <span class="eyebrow"><span></span>Legal</span>
      <h1>Privacy Policy</h1>
      <p class="legal-updated">Last updated: July 24, 2026</p>

      <p>CalmDecide respects your privacy. This policy explains what information we collect, how we use it, when it may be shared, and how advertising partners such as Google may use cookies or similar technologies if ads are enabled on calmdecide.com.</p>

      <div class="legal-callout">
        <strong>Plain-language summary</strong>
        <p>We collect only what is needed to run accounts, decision tools, community features, reminders, security, and site operations. We do not sell personal information. If ads are shown, third-party advertising partners may use cookies and identifiers as described below.</p>
      </div>

      <h2>Who is responsible for your information</h2>
      <p>CalmDecide is responsible for the personal information described in this policy. References to "CalmDecide," "we," "our," or "us" mean the operator of calmdecide.com. You can contact us at <a :href="mailLink">{{ email }}</a>.</p>

      <h2>Information we collect</h2>
      <p>When you create an account, we collect your name, email address, and an encrypted password. When you save a decision, we store the information you choose to enter, including titles, options, scores, selected outcomes, reflections, review notes, and reminder status.</p>
      <p>Community polls and passing notes may be used without an account. We may store a one-way technical identifier derived from your IP address and browser information to reduce duplicate voting, spam, and abuse. We do not store this identifier in a form designed to reveal your original IP address.</p>

      <h3>Information you provide directly</h3>
      <ul>
        <li>Account details such as name, email address, and password.</li>
        <li>Decision content such as titles, options, criteria, weights, scores, reflections, selected outcomes, and later review notes.</li>
        <li>Community content such as poll questions, option text, uploaded images, votes, and anonymous passing notes.</li>
        <li>Messages you send for support, content reports, feedback, or privacy requests.</li>
      </ul>

      <h3>Information collected automatically</h3>
      <p>Our servers and infrastructure providers may process basic technical information when you use the site, including IP address, browser type, device type, requested pages, timestamps, error information, and referring page. This information is used for security, troubleshooting, rate limiting, and service reliability.</p>

      <h2>How we use information</h2>
      <p>We use information to operate and secure CalmDecide, authenticate accounts, save decisions, display journals and dashboards, deliver requested reminders, moderate community features, investigate errors, respond to legal obligations, and understand aggregate usage patterns.</p>
      <p>We do not intentionally pass names, email addresses, saved decision text, or other directly identifying account information to Google in ad requests.</p>

      <h2>Cookies, local storage, and advertising</h2>
      <p>CalmDecide uses browser local storage to keep users signed in. Essential authentication storage is required for account features. Removing local browser data or using private-browsing mode may sign you out.</p>
      <p>CalmDecide may display advertising served by Google or other third-party advertising partners on pages that contain publisher content, such as original decision guides. These partners may use cookies, web beacons, IP addresses, device information, browser information, and similar identifiers to serve ads, measure ad performance, prevent fraud, and comply with policy requirements.</p>
      <p>Google's use of advertising cookies enables Google and its partners to serve ads based on visits to this site and/or other sites on the internet. You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>. You can also learn how Google uses information from sites and apps that use Google services at <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">Google's partner-sites policy page</a>.</p>
      <p>Where required by law, we will request consent before using non-essential advertising cookies or personalized advertising. Users in regions with consent requirements should see an appropriate consent choice before personalized ads are enabled.</p>

      <h2>When information may be shared</h2>
      <p>We do not sell or rent personal information. We may share limited information with hosting, database, email, security, analytics, or technical service providers acting on our instructions. Public polls and passing notes are visible to other visitors. We may also disclose information when reasonably necessary to comply with law, protect rights or safety, investigate abuse, or complete a legitimate business transfer.</p>

      <h2>Retention</h2>
      <div class="legal-table-wrap">
        <table class="legal-table">
          <thead><tr><th>Information</th><th>Typical retention</th></tr></thead>
          <tbody>
            <tr><td>Account and saved decisions</td><td>Until the account or record is deleted, subject to backup and legal requirements.</td></tr>
            <tr><td>Authentication tokens</td><td>Until expiration, sign-out, or security invalidation.</td></tr>
            <tr><td>Public polls and notes</td><td>While useful to the community, unless removed for moderation or upon a valid request.</td></tr>
            <tr><td>Rate-limit and security records</td><td>Normally short-lived unless needed to investigate abuse or a security event.</td></tr>
            <tr><td>Support correspondence</td><td>As long as reasonably needed to resolve and document the request.</td></tr>
          </tbody>
        </table>
      </div>

      <h2>Your choices</h2>
      <p>You may request access, correction, export, or deletion of your account and saved decisions. Community posts are public and should never contain private or sensitive information. Depending on where you live, you may also have rights to restrict or object to certain processing, withdraw consent, or complain to a data-protection authority.</p>
      <p>To make a request, email <a :href="privacyMailLink">{{ email }}</a> with the subject "Privacy Request." Describe the account email and the action you want us to take. Do not send your password.</p>

      <h2>Children and sensitive information</h2>
      <p>CalmDecide is not intended for children under 13 and is not designed to collect government identification numbers, payment card information, precise location, health records, biometric information, or other highly sensitive personal information. Please do not include such information in decisions, community posts, or support messages.</p>

      <h2>Security and international processing</h2>
      <p>We use reasonable technical safeguards, including password hashing and restricted API access. No internet service can guarantee absolute security. CalmDecide and its infrastructure providers may process information in countries other than the one where you live, subject to applicable safeguards.</p>

      <h2>Third-party links and services</h2>
      <p>CalmDecide may link to or display information from third-party services, such as places, movie, or advertising providers. Their privacy practices are governed by their own policies.</p>

      <h2>Changes and contact</h2>
      <p>We may update this policy when CalmDecide changes or legal requirements evolve. We will update the date above and provide additional notice when appropriate. For privacy questions, email <a :href="mailLink">{{ email }}</a>.</p>
    </article>

    <article v-else-if="page === 'terms'">
      <span class="eyebrow"><span></span>Legal</span>
      <h1>Terms of Use</h1>
      <p class="legal-updated">Last updated: July 24, 2026</p>

      <p>By using CalmDecide, you agree to these terms. If you do not agree, please do not use the service.</p>

      <div class="legal-callout">
        <strong>Short version</strong>
        <p>CalmDecide helps organize choices, but you remain responsible for decisions. Use the tools and community respectfully, protect your account, and do not use the service to harm others or break the law.</p>
      </div>

      <h2>What CalmDecide is</h2>
      <p>CalmDecide provides decision tools, original decision-making guides, community polls, anonymous notes, saved decision journals, and reminder features. The service is an informational and organizational aid, not a professional adviser.</p>

      <h2>Eligibility and accounts</h2>
      <p>You must be legally capable of agreeing to these terms in your location. If you are below the age required to consent to an online service where you live, you may use CalmDecide only with permission from a parent or legal guardian. You are responsible for keeping account credentials secure and for activity under your account.</p>

      <h2>No professional advice</h2>
      <p>Do not rely on a randomizer, community poll, weighted score, article, or generated report for emergencies, dangerous activities, investments, medical treatment, legal rights, crisis support, or any situation requiring qualified professional judgment. If you are in immediate danger or experiencing a crisis, contact local emergency services or an appropriate professional.</p>

      <h2>User content and community rules</h2>
      <p>You retain ownership of content you submit. You grant CalmDecide a limited, non-exclusive, worldwide license to host, store, reproduce, process, and display that content only as needed to operate, secure, and improve the service. Public community content may be visible to other users.</p>
      <ul>
        <li>Do not post illegal, hateful, harassing, violent, sexually explicit, deceptive, infringing, or privacy-invasive content.</li>
        <li>Do not upload images or text you do not have the right to share.</li>
        <li>Do not spam, manipulate votes, scrape the service, or interfere with security.</li>
        <li>Do not request or publish someone else's private personal information.</li>
      </ul>

      <h2>Advertising and third-party services</h2>
      <p>The service may display advertising or link to third-party providers. Ads and third-party services are governed by their own terms and policies. We do not endorse every advertised product or third-party result, and we are not responsible for their availability, security, content, or privacy practices.</p>

      <h2>Intellectual property</h2>
      <p>The CalmDecide name, visual design, software, original guides, text, and service materials are owned by or licensed to the service operator and protected by applicable intellectual-property laws. These terms give you a personal, limited, revocable, non-transferable right to use the service; they do not transfer ownership.</p>

      <h2>Changes, availability, and termination</h2>
      <p>CalmDecide is provided "as is" and "as available." We may update, pause, limit, or discontinue features. We may remove content or suspend access if we believe these terms, law, safety, or platform policies require it. Continued use after an update means you accept the revised terms.</p>

      <h2>Disclaimers and liability</h2>
      <p>To the fullest extent permitted by law, CalmDecide disclaims warranties of accuracy, fitness for a particular purpose, merchantability, non-infringement, availability, and results. We do not guarantee that a recommendation, score, vote, reminder, guide, or community response will be correct. Nothing in these terms limits rights that cannot legally be waived.</p>

      <h2>Contact</h2>
      <p>Questions about these terms may be sent to <a :href="mailLink">{{ email }}</a>.</p>
    </article>

    <article v-else class="contact-page">
      <span class="eyebrow"><span></span>Contact</span>
      <h1>Contact Us</h1>
      <p>Questions, feedback, privacy requests, content reports, or thoughtful ideas for CalmDecide are welcome.</p>
      <a class="contact-card" :href="mailLink">
        <span>EMAIL</span>
        <strong>{{ email }}</strong>
        <i>→</i>
      </a>
      <p class="contact-note">Please do not include passwords, payment details, health records, government IDs, or other highly sensitive information in your message.</p>

      <div class="contact-sections">
        <section>
          <span>SUPPORT</span>
          <h2>Product questions</h2>
          <p>Send the page URL, what you expected, what happened, and your browser or device if it helps reproduce the issue.</p>
          <a :href="supportMailLink">Ask for support →</a>
        </section>
        <section>
          <span>PRIVACY</span>
          <h2>Account or data requests</h2>
          <p>Use the subject "Privacy Request" and contact us from the email associated with your account. State whether you want access, correction, export, or deletion.</p>
          <a :href="privacyMailLink">Make a privacy request →</a>
        </section>
        <section>
          <span>COMMUNITY</span>
          <h2>Report content</h2>
          <p>Include the poll question, note text, approximate posting time, and why it may violate the community rules. We review reports and take action when appropriate.</p>
          <a :href="reportMailLink">Report content →</a>
        </section>
        <section>
          <span>FEEDBACK</span>
          <h2>Suggest improvements</h2>
          <p>Ideas for calmer flows, new decision frameworks, accessibility improvements, or editorial topics help shape the product.</p>
          <a :href="feedbackMailLink">Send feedback →</a>
        </section>
      </div>

      <div class="response-note">
        <strong>Response expectations</strong>
        <p>We aim to respond as soon as reasonably possible. Privacy and safety requests are prioritized. CalmDecide cannot provide personal medical, legal, financial, or crisis advice.</p>
      </div>
    </article>
  </section>
</template>

<script>
export default {
  name: 'LegalView',
  inject: ['navigate'],
  props: {
    page: { type: String, required: true }
  },
  data: () => ({
    email: 'tudouchenxiaoze@gmail.com'
  }),
  computed: {
    mailLink () { return `mailto:${this.email}` },
    privacyMailLink () { return `mailto:${this.email}?subject=Privacy%20Request` },
    supportMailLink () { return `mailto:${this.email}?subject=CalmDecide%20Support` },
    reportMailLink () { return `mailto:${this.email}?subject=Content%20Report` },
    feedbackMailLink () { return `mailto:${this.email}?subject=CalmDecide%20Feedback` }
  }
}
</script>
